/**
 * Group learning (docs/adr/0036-group-learning.md): a group owner runs training across the companies
 * of the group from one place — one library of every company's courses, copy/assign a course to
 * several companies at once, and one calendar of every company's live sessions with the actions an
 * HR admin has in each company.
 *
 * Every route is `routeMeta('learning.group:manage', hidden)`. That permission is only the entry
 * gate: the API also requires the caller to own the group, and, for EACH company a call touches,
 * `learning:manage` company-wide for the caller's own account there, read live (ADR 0036 §2). A
 * company that fails the check is reported as "can't manage" on the read routes and skipped by a run;
 * it never fails the whole call.
 *
 * Company ids in a path or body only ever CHOOSE AMONG the caller's group's members (ADR 0034 §7);
 * the API reads membership itself. Courses are copied, never shared (ADR 0036 §1), so every course
 * and live session in a response belongs to exactly one company.
 *
 * Kept out of the AI copilot (`ai: 'hidden'`): a cross-company assign is not a proposal a model
 * should draft, and the response carries every company's roster counts.
 *
 * Contract-only per invariant 8: these routes land before the handlers that implement them.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  dateOnlySchema,
  errorBodySchema,
  idempotencyKeySchema,
  instantSchema,
  orgIdSchema,
  timezoneSchema,
  trainingLiveSessionProviderSchema,
  trainingLiveSessionStatusSchema,
  uuidSchema,
} from '../schemas/common.js';
import { routeMeta } from './meta.js';
import {
  trainingAssignmentDueSchema,
  trainingAssignmentSkipReasonSchema,
  trainingAudiencePreviewResponseSchema,
  trainingAudienceSchema,
} from './training.contract.js';
import {
  addTrainingLiveInviteesBodySchema,
  cancelTrainingLiveSessionBodySchema,
  trainingLiveAttendanceCountsSchema,
  trainingLiveSessionDetailSchema,
  trainingLiveSessionDisplayStatusSchema,
  updateTrainingLiveSessionBodySchema,
} from './training-live.contract.js';

const c = initContract();

const idempotencyHeadersSchema = z.object({
  'idempotency-key': idempotencyKeySchema,
});

/** At most this many companies in one run. A group is a handful of companies, not hundreds. */
export const GROUP_LEARNING_MAX_TARGETS = 50;

/** A run item that has been `copying` or `assigning` this long without finishing is shown as
 *  `interrupted` (the API restarted, or hung). Computed when read; nothing needs to notice. */
export const GROUP_LEARNING_ITEM_STALE_MINUTES = 10;

/** The longest calendar window one read returns. */
export const GROUP_LEARNING_CALENDAR_MAX_DAYS = 93;

// ---------------------------------------------------------------------------
// Companies
// ---------------------------------------------------------------------------

/** Why a group company cannot be used right now. Plain language for it is in `reasonText`. */
export const GROUP_LEARNING_COMPANY_REASONS = ['no_access', 'training_off'] as const;
export const groupLearningCompanyReasonSchema = z.enum(GROUP_LEARNING_COMPANY_REASONS);
export type GroupLearningCompanyReason = z.infer<typeof groupLearningCompanyReasonSchema>;

/** One company of the group, as this caller sees it for training. */
export const groupLearningCompanySchema = z.object({
  orgId: orgIdSchema,
  name: z.string(),
  /** The company's web address label — the web app builds the company's own address from it. */
  slug: z.string(),
  /** Training is available there AND the caller holds `learning:manage` company-wide in it. */
  canManage: z.boolean(),
  /** Set when `canManage` is false. */
  reason: groupLearningCompanyReasonSchema.optional(),
  /** "You can't manage training here" / "Training isn't available in this company". */
  reasonText: z.string().optional(),
});
export type GroupLearningCompany = z.infer<typeof groupLearningCompanySchema>;

// ---------------------------------------------------------------------------
// Course library
// ---------------------------------------------------------------------------

/**
 * One company's state for one course row. `published` is the stored `active` status. `no_access` and
 * `training_off` are the whole company's state, repeated on every row so the table can grey the
 * column; they carry none of the optional fields.
 */
export const GROUP_LEARNING_CELL_STATES = [
  'not_there',
  'draft',
  'published',
  'archived',
  'no_access',
  'training_off',
] as const;
export const groupLearningCellStateSchema = z.enum(GROUP_LEARNING_CELL_STATES);
export type GroupLearningCellState = z.infer<typeof groupLearningCellStateSchema>;

export const groupLearningCourseCellSchema = z.object({
  orgId: orgIdSchema,
  state: groupLearningCellStateSchema,
  /** The course's latest version in that company. Absent for `not_there`, `no_access`, `training_off`. */
  taskId: uuidSchema.optional(),
  /** That course's lineage in that company. */
  lineageId: uuidSchema.optional(),
  /** Published only: people assigned / completed across every version. */
  assignedCount: z.number().int().optional(),
  completedCount: z.number().int().optional(),
  /** The latest version number in that company. */
  version: z.number().int().optional(),
  /** A copy whose source has a newer version than the one copied ("Source updated"). */
  outOfDate: z.boolean().optional(),
});
export type GroupLearningCourseCell = z.infer<typeof groupLearningCourseCellSchema>;

/** One row of the library: a course and the copies made from it, one cell per company. */
export const groupLearningCourseRowSchema = z.object({
  /** Stable row key: the lineage id of the course the row started from (its origin). */
  rowId: uuidSchema,
  title: z.string(),
  /** The origin's category name (copies match categories by name). */
  categoryName: z.string().nullable(),
  /** The origin has a strict switch on (webcam, clock-out lock, strict playback) — the Assign
   *  drawer warns that a copy keeps those settings whatever the target company's policy. */
  isStrict: z.boolean(),
  /** The company the course originated in. */
  sourceOrgId: orgIdSchema,
  /** One entry per company in `companies`, same order. */
  cells: z.array(groupLearningCourseCellSchema),
});
export type GroupLearningCourseRow = z.infer<typeof groupLearningCourseRowSchema>;

export const listGroupLearningCoursesResponseSchema = z.object({
  groupName: z.string(),
  companies: z.array(groupLearningCompanySchema),
  courses: z.array(groupLearningCourseRowSchema),
});
export type ListGroupLearningCoursesResponse = z.infer<
  typeof listGroupLearningCoursesResponseSchema
>;

// ---------------------------------------------------------------------------
// Audience, per company
// ---------------------------------------------------------------------------

/** What one company offers for "Who": its own legal entities, departments and locations. */
export const groupLearningAudienceOptionsSchema = z.object({
  orgId: orgIdSchema,
  legalEntities: z.array(z.object({ id: uuidSchema, name: z.string() })),
  departments: z.array(z.object({ id: uuidSchema, name: z.string() })),
  locations: z.array(z.object({ id: uuidSchema, name: z.string() })),
});
export type GroupLearningAudienceOptions = z.infer<typeof groupLearningAudienceOptionsSchema>;

/** How many people an audience would reach in one company (the Review step). Ids in `audience` are
 *  that company's own; an id from another company matches nobody. */
export const groupLearningAudiencePreviewBodySchema = z.object({
  audience: trainingAudienceSchema,
});
export type GroupLearningAudiencePreviewBody = z.infer<
  typeof groupLearningAudiencePreviewBodySchema
>;
export type GroupLearningAudiencePreview = z.infer<typeof trainingAudiencePreviewResponseSchema>;

// ---------------------------------------------------------------------------
// Runs
// ---------------------------------------------------------------------------

export const GROUP_LEARNING_RUN_KINDS = ['copy', 'assign'] as const;
export const groupLearningRunKindSchema = z.enum(GROUP_LEARNING_RUN_KINDS);
export type GroupLearningRunKind = z.infer<typeof groupLearningRunKindSchema>;

/** Derived from the items: `partial` = at least one company done and at least one not. */
export const GROUP_LEARNING_RUN_STATUSES = [
  'queued',
  'running',
  'done',
  'partial',
  'failed',
] as const;
export const groupLearningRunStatusSchema = z.enum(GROUP_LEARNING_RUN_STATUSES);
export type GroupLearningRunStatus = z.infer<typeof groupLearningRunStatusSchema>;

/**
 * Where one company's part of a run is. `queued` = not started. `interrupted` is computed when read
 * (a `copying`/`assigning` item untouched for `GROUP_LEARNING_ITEM_STALE_MINUTES`) and may also be
 * stored. Retry is offered for `failed`, `interrupted` and a `skipped` item whose reason can change.
 */
export const GROUP_LEARNING_ITEM_STEPS = [
  'queued',
  'copying',
  'assigning',
  'done',
  'failed',
  'skipped',
  'interrupted',
] as const;
export const groupLearningItemStepSchema = z.enum(GROUP_LEARNING_ITEM_STEPS);
export type GroupLearningItemStep = z.infer<typeof groupLearningItemStepSchema>;

/** Why a whole company was skipped. (Per-person skips use `trainingAssignmentSkipReasonSchema`.) */
export const GROUP_LEARNING_SKIP_REASONS = [
  'no_access',
  'training_off',
  'source_unavailable',
] as const;
export const groupLearningSkipReasonSchema = z.enum(GROUP_LEARNING_SKIP_REASONS);
export type GroupLearningSkipReason = z.infer<typeof groupLearningSkipReasonSchema>;

/** The course to copy or assign: a course in one company of the group. */
export const groupLearningSourceSchema = z.object({
  orgId: orgIdSchema,
  taskId: uuidSchema,
});
export type GroupLearningSource = z.infer<typeof groupLearningSourceSchema>;

/**
 * What the owner asked for in ONE company. `audience`/`due` are required for an `assign` run and
 * ignored by a `copy` run. Ids inside `audience` (legal entities, departments, locations, people) are
 * that company's own and are checked against it. `due` is the existing training due shape: fixed
 * date, N days after joining, or none.
 */
export const groupLearningRunTargetSchema = z.object({
  orgId: orgIdSchema,
  audience: trainingAudienceSchema.optional(),
  due: trainingAssignmentDueSchema.optional(),
  isMandatory: z.boolean().default(true),
  autoEnrolNewJoiners: z.boolean().default(false),
});
export type GroupLearningRunTarget = z.infer<typeof groupLearningRunTargetSchema>;

export const createGroupLearningRunBodySchema = z
  .object({
    kind: groupLearningRunKindSchema,
    source: groupLearningSourceSchema,
    /** One entry per company. The source's own company may be a target of an `assign` run (no copy). */
    targets: z.array(groupLearningRunTargetSchema).min(1).max(GROUP_LEARNING_MAX_TARGETS),
  })
  .superRefine((body, ctx) => {
    const seen = new Set<string>();
    body.targets.forEach((target, index) => {
      if (seen.has(target.orgId)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['targets', index, 'orgId'],
          message: 'Each company can appear only once',
        });
      }
      seen.add(target.orgId);
      if (body.kind === 'assign' && (!target.audience || !target.due)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['targets', index, target.audience ? 'due' : 'audience'],
          message: 'Choose who to assign to and when it is due',
        });
      }
    });
  });
export type CreateGroupLearningRunBody = z.infer<typeof createGroupLearningRunBodySchema>;

export const groupLearningRunItemSchema = z.object({
  orgId: orgIdSchema,
  companyName: z.string(),
  step: groupLearningItemStepSchema,
  /** Set when `step` is `skipped`. */
  skipReason: groupLearningSkipReasonSchema.nullable(),
  /** People assigned / not assigned in that company (an assign run), with the existing skip reasons. */
  assignedCount: z.number().int(),
  skippedCount: z.number().int(),
  skippedReasons: z.array(
    z.object({ reason: trainingAssignmentSkipReasonSchema, count: z.number().int() }),
  ),
  /** The course in that company once it exists (copied or reused). */
  targetTaskId: uuidSchema.nullable(),
  /** Short, plain-language, safe to show. Never a stack trace or a database message. */
  error: z.string().nullable(),
  /** Non-fatal notes ("Skills are not copied", "Uses a different policy there"). */
  warnings: z.array(z.string()),
  attempts: z.number().int(),
  updatedAt: instantSchema,
});
export type GroupLearningRunItem = z.infer<typeof groupLearningRunItemSchema>;

export const groupLearningRunSchema = z.object({
  id: uuidSchema,
  kind: groupLearningRunKindSchema,
  source: groupLearningSourceSchema.extend({ courseTitle: z.string(), companyName: z.string() }),
  status: groupLearningRunStatusSchema,
  createdAt: instantSchema,
  finishedAt: instantSchema.nullable(),
  items: z.array(groupLearningRunItemSchema),
});
export type GroupLearningRun = z.infer<typeof groupLearningRunSchema>;

/**
 * The step to SHOW for a run item: a `copying`/`assigning` item untouched for
 * `GROUP_LEARNING_ITEM_STALE_MINUTES` is `interrupted` (the process that was doing it is gone).
 * `touchedAt` is the item's `updatedAt`. Pure, so the API and the web agree.
 */
export function effectiveGroupLearningItemStep(
  step: GroupLearningItemStep,
  touchedAt: Date,
  now: Date = new Date(),
): GroupLearningItemStep {
  if (step !== 'copying' && step !== 'assigning') return step;
  const idleMs = now.getTime() - touchedAt.getTime();
  return idleMs > GROUP_LEARNING_ITEM_STALE_MINUTES * 60_000 ? 'interrupted' : step;
}

/**
 * A run's status from its items' (effective) steps. Nothing started = `queued`; any item still
 * `queued`, `copying` or `assigning` = `running`; otherwise all `done` = `done`, none `done` =
 * `failed`, a mix = `partial`. A `skipped` item counts as not done. An `interrupted` item is not
 * running: it waits for Retry.
 */
export function summariseGroupLearningRunStatus(
  steps: readonly GroupLearningItemStep[],
): GroupLearningRunStatus {
  if (steps.length === 0 || steps.every((step) => step === 'queued')) return 'queued';
  if (steps.some((step) => step === 'queued' || step === 'copying' || step === 'assigning')) {
    return 'running';
  }
  const done = steps.filter((step) => step === 'done').length;
  if (done === steps.length) return 'done';
  return done === 0 ? 'failed' : 'partial';
}

// ---------------------------------------------------------------------------
// Live-session calendar
// ---------------------------------------------------------------------------

const uuidCsvQuerySchema = z
  .union([z.array(orgIdSchema), z.string()])
  .transform((value) =>
    Array.isArray(value)
      ? value
      : value
          .split(',')
          .map((id) => id.trim())
          .filter((id) => id !== ''),
  )
  .pipe(z.array(orgIdSchema).max(GROUP_LEARNING_MAX_TARGETS));

export const listGroupLiveSessionsQuerySchema = z
  .object({
    /** First and last day shown (inclusive), as dates in `timezone`. */
    from: dateOnlySchema,
    to: dateOnlySchema,
    /** The viewer's IANA zone, which decides what "the day" means for `from`/`to`. Defaults to UTC. */
    timezone: timezoneSchema.optional(),
    /** Only these companies (comma-separated). Omitted = every company of the group. */
    orgIds: uuidCsvQuerySchema.optional(),
  })
  .superRefine((query, ctx) => {
    const from = Date.parse(`${query.from}T00:00:00Z`);
    const to = Date.parse(`${query.to}T00:00:00Z`);
    if (Number.isNaN(from) || Number.isNaN(to) || to < from) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['to'],
        message: 'The end must not be before the start',
      });
    } else if ((to - from) / 86_400_000 + 1 > GROUP_LEARNING_CALENDAR_MAX_DAYS) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['to'],
        message: `Choose at most ${GROUP_LEARNING_CALENDAR_MAX_DAYS} days`,
      });
    }
  });
export type ListGroupLiveSessionsQuery = z.infer<typeof listGroupLiveSessionsQuerySchema>;

/** One live session on the calendar, with the company it belongs to. */
export const groupLiveSessionEntrySchema = z.object({
  orgId: orgIdSchema,
  companyName: z.string(),
  sessionId: uuidSchema,
  title: z.string(),
  taskId: uuidSchema.nullable(),
  taskTitle: z.string().nullable(),
  startsAt: instantSchema,
  endsAt: instantSchema,
  /** IANA zone the session was scheduled in — the side panel shows its own time in it. */
  timezone: z.string(),
  hostName: z.string(),
  provider: trainingLiveSessionProviderSchema,
  status: trainingLiveSessionStatusSchema,
  displayStatus: trainingLiveSessionDisplayStatusSchema,
  counts: trainingLiveAttendanceCountsSchema,
  /** The company's own shareable join link (sign-in required) — what "Copy join link" copies. */
  shareUrl: z.string().url(),
});
export type GroupLiveSessionEntry = z.infer<typeof groupLiveSessionEntrySchema>;

export const listGroupLiveSessionsResponseSchema = z.object({
  /** Every company of the group, with whether the caller can manage training there. Sessions of a
   *  company the caller cannot manage are not returned. */
  companies: z.array(groupLearningCompanySchema),
  sessions: z.array(groupLiveSessionEntrySchema),
});
export type ListGroupLiveSessionsResponse = z.infer<typeof listGroupLiveSessionsResponseSchema>;

/** A company's own session detail, plus notes about what could not be done there. */
export const groupLiveSessionActionResponseSchema = trainingLiveSessionDetailSchema.extend({
  /** E.g. "Zoho isn't connected in this company, so the Zoho meeting was not changed." */
  notices: z.array(z.string()).optional(),
});
export type GroupLiveSessionActionResponse = z.infer<typeof groupLiveSessionActionResponseSchema>;

/** Reschedule or change settings: the company's own update body, unchanged. */
export const updateGroupLiveSessionBodySchema = updateTrainingLiveSessionBodySchema;
export type UpdateGroupLiveSessionBody = z.infer<typeof updateGroupLiveSessionBodySchema>;
/** Invite more people, departments or locations: the company's own body, unchanged. */
export const inviteGroupLiveSessionBodySchema = addTrainingLiveInviteesBodySchema;
export type InviteGroupLiveSessionBody = z.infer<typeof inviteGroupLiveSessionBodySchema>;
/** Cancel with a reason: the company's own body, unchanged. */
export const cancelGroupLiveSessionBodySchema = cancelTrainingLiveSessionBodySchema;
export type CancelGroupLiveSessionBody = z.infer<typeof cancelGroupLiveSessionBodySchema>;

// ---------------------------------------------------------------------------
// Routes
// ---------------------------------------------------------------------------

const errors = {
  400: errorBodySchema,
  401: errorBodySchema,
  403: errorBodySchema,
  404: errorBodySchema,
  409: errorBodySchema,
} as const;

const hidden = { ai: 'hidden' } as const;
const groupLearning = routeMeta('learning.group:manage', hidden);

const orgPathParams = z.object({ orgId: orgIdSchema });
const sessionPathParams = z.object({ orgId: orgIdSchema, sessionId: uuidSchema });

export const groupLearningContract = c.router(
  {
    listCourses: {
      method: 'GET',
      path: '/courses',
      responses: { 200: listGroupLearningCoursesResponseSchema, ...errors },
      summary:
        "Every group company's courses in one library: one row per course and its copies, one " +
        "cell per company with that company's state and progress",
      metadata: groupLearning,
    },

    audienceOptions: {
      method: 'GET',
      path: '/companies/:orgId/audience-options',
      pathParams: orgPathParams,
      responses: { 200: groupLearningAudienceOptionsSchema, ...errors },
      summary:
        "One company's legal entities, departments and locations, for the per-company Who step",
      metadata: groupLearning,
    },

    audiencePreview: {
      method: 'POST',
      path: '/companies/:orgId/audience-preview',
      pathParams: orgPathParams,
      body: groupLearningAudiencePreviewBodySchema,
      responses: { 200: trainingAudiencePreviewResponseSchema, ...errors },
      summary: 'How many people an audience reaches in one company, for the Review step',
      metadata: groupLearning,
    },

    createRun: {
      method: 'POST',
      path: '/runs',
      headers: idempotencyHeadersSchema,
      body: createGroupLearningRunBodySchema,
      responses: { 202: groupLearningRunSchema, ...errors },
      summary:
        'Copy a course to several companies, or assign it there (copying first where it is not ' +
        'yet). Runs in the background one company at a time; a retried request returns the same run',
      metadata: groupLearning,
    },

    getRun: {
      method: 'GET',
      path: '/runs/:id',
      pathParams: z.object({ id: uuidSchema }),
      responses: { 200: groupLearningRunSchema, ...errors },
      summary: 'Progress of a run, per company',
      metadata: groupLearning,
    },

    retryRunItem: {
      method: 'POST',
      path: '/runs/:id/items/:orgId/retry',
      pathParams: z.object({ id: uuidSchema, orgId: orgIdSchema }),
      body: z.object({}),
      responses: { 202: groupLearningRunSchema, ...errors },
      summary:
        "Run one company's part of a run again (failed, interrupted, or skipped for rights that " +
        'may have changed). Never copies or assigns twice',
      metadata: groupLearning,
    },

    listLiveSessions: {
      method: 'GET',
      path: '/live-sessions',
      query: listGroupLiveSessionsQuerySchema,
      responses: { 200: listGroupLiveSessionsResponseSchema, ...errors },
      summary:
        "Every group company's live sessions and webinars in a date range, for the calendar",
      metadata: groupLearning,
    },

    getLiveSession: {
      method: 'GET',
      path: '/live-sessions/:orgId/:sessionId',
      pathParams: sessionPathParams,
      responses: { 200: groupLiveSessionActionResponseSchema, ...errors },
      summary:
        "One company's live session with its invitees and settings, for the calendar's side panel",
      metadata: groupLearning,
    },

    updateLiveSession: {
      method: 'PATCH',
      path: '/live-sessions/:orgId/:sessionId',
      pathParams: sessionPathParams,
      body: updateGroupLiveSessionBodySchema,
      responses: { 200: groupLiveSessionActionResponseSchema, ...errors, 503: errorBodySchema },
      summary:
        "Reschedule or change the settings of a live session in that company, as the caller's own " +
        'account there (updates the Zoho meeting when connected)',
      metadata: groupLearning,
    },

    inviteToLiveSession: {
      method: 'POST',
      path: '/live-sessions/:orgId/:sessionId/invitees',
      pathParams: sessionPathParams,
      body: inviteGroupLiveSessionBodySchema,
      responses: { 200: groupLiveSessionActionResponseSchema, ...errors },
      summary:
        "Invite more of that company's people, departments or locations; people already invited " +
        'are skipped',
      metadata: groupLearning,
    },

    cancelLiveSession: {
      method: 'POST',
      path: '/live-sessions/:orgId/:sessionId/cancel',
      pathParams: sessionPathParams,
      body: cancelGroupLiveSessionBodySchema,
      responses: { 200: groupLiveSessionActionResponseSchema, ...errors },
      summary: 'Cancel a live session in that company, with a reason',
      metadata: groupLearning,
    },
  },
  { pathPrefix: '/company-group/learning' },
);

export type GroupLearningContract = typeof groupLearningContract;
